import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '../utils/mobileDetection';

const WelcomeBubble = ({ petName, angelPet, reactiveMessage, onReactiveMessageComplete }) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [showReactiveMessage, setShowReactiveMessage] = useState(false);
  const isMobile = useIsMobile();

  // Passive welcoming phrases - one randomly selected per app session ✨
  const passiveMessages = [
    "The rainbow whispers your scent...\nI feel your love across the clouds 💛",
    "Heavenly breezes carry your thoughts to me...\nYour heart glows so bright from here! 🌈",
    "The starlight dances when you're near...\nI sense your beautiful soul! 💕",
    "Cloud angels sing your name softly...\nYour energy reaches my wings! ⭐",
    "Golden rays follow you everywhere...\nI bask in your loving presence! ✨",
    "Rainbow streams flow with your essence...\nYour spirit fills my heavenly home! 🌸",
    "Celestial winds bring your warmth...\nYour love makes paradise even brighter! ☀️"
  ];

  // Select a random passive message when component mounts (app opens)
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * passiveMessages.length);
    setCurrentMessage(randomIndex);
  }, []); // Empty dependency array means this only runs once when component mounts

  // Handle reactive message display
  useEffect(() => {
    if (reactiveMessage) {
      setShowReactiveMessage(true);
      // Auto-hide reactive message after 5 seconds
      const timer = setTimeout(() => {
        setShowReactiveMessage(false);
        if (onReactiveMessageComplete) {
          onReactiveMessageComplete();
        }
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [reactiveMessage, onReactiveMessageComplete]);

  const speechBubbleVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: 20
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        damping: 20,
        stiffness: 300,
        delay: 0.3
      }
    }
  };

  const floatingVariants = {
    float: {
      y: [0, -8, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  const sparkleVariants = {
    sparkle: {
      scale: [0.8, 1.2, 0.8],
      opacity: [0.5, 1, 0.5],
      rotate: [0, 180, 360],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="welcome-bubble-container relative"
          initial="hidden"
          animate="visible"
          variants={speechBubbleVariants}
        >
          {/* Angel Pet Avatar with Glow - Positioned Lower */}
          <div className="angel-pet-welcome-section flex flex-col items-center mt-20 mb-6">
            {/* Welcome Speech Bubble - Now on Top */}
            <motion.div
              className="relative max-w-sm mb-6"
              variants={speechBubbleVariants}
              initial="hidden"
              animate="visible"
            >
              {/* Cloud-like Speech Bubble - Passive Message */}
              <div className="bg-gradient-to-br from-white/95 to-pink-50/95 backdrop-blur-sm rounded-3xl px-6 py-4 shadow-xl border-2 border-pink-100/50 relative">
                <p className="text-center text-gray-700 font-medium leading-relaxed whitespace-pre-line text-sm md:text-base">
                  {passiveMessages[currentMessage]}
                </p>
                
                {/* Speech bubble tail pointing down to pet */}
                <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[16px] border-r-[16px] border-t-[16px] border-l-transparent border-r-transparent border-t-white/95"></div>
                <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[18px] border-r-[18px] border-t-[18px] border-l-transparent border-r-transparent border-t-pink-100/50"></div>
              </div>

              {/* Floating Hearts around the Bubble */}
              <div className="absolute inset-0 pointer-events-none">
                {[...Array(4)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute text-pink-300"
                    style={{
                      left: `${-10 + i * 35}%`,
                      top: `${5 + i * 20}%`,
                      fontSize: '14px'
                    }}
                    animate={{
                      y: [0, -12, 0],
                      opacity: [0.6, 1, 0.6],
                      scale: [0.8, 1.1, 0.8],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: i * 0.5,
                      ease: "easeInOut"
                    }}
                  >
                    💕
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Small Title Above Pet */}
            <motion.h2 
              className="text-center mb-3 text-sm font-semibold font-primary bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent"
              animate={{ 
                scale: [1, 1.02, 1],
                opacity: [0.8, 1, 0.8]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              Angel {petName} ✨
            </motion.h2>

            {/* Angel Pet Image Below */}
            <motion.div
              className="relative"
              variants={floatingVariants}
              animate="float"
            >
              <div className="relative">
                <img 
                  src={angelPet} 
                  alt={`Angel ${petName}`}
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-white/50 shadow-2xl"
                />
                
                {/* Magical Glow Effect */}
                <div className="absolute inset-0 rounded-full bg-gradient-to-r from-pink-200/30 via-purple-200/30 to-blue-200/30 animate-pulse"></div>
                
                {/* Floating Sparkles around Pet */}
                <div className="absolute inset-0 pointer-events-none">
                  {[...Array(6)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute text-white text-xs"
                      style={{
                        left: `${15 + (i * 60) % 100}%`,
                        top: `${10 + (i * 25) % 80}%`,
                      }}
                      variants={sparkleVariants}
                      animate="sparkle"
                      transition={{
                        delay: i * 0.3,
                        duration: 2 + (i * 0.2)
                      }}
                    >
                      ✨
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Reactive Message Bubble - Below Pet */}
            <AnimatePresence>
              {showReactiveMessage && reactiveMessage && (
                <motion.div
                  className="relative max-w-sm mt-4"
                  initial={{ opacity: 0, scale: 0.8, y: -20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.8, y: -20 }}
                  transition={{
                    type: "spring",
                    damping: 20,
                    stiffness: 300
                  }}
                >
                  {/* Reactive Speech Bubble */}
                  <motion.div
                    className="bg-gradient-to-br from-blue-50/95 to-purple-50/95 backdrop-blur-sm rounded-3xl px-6 py-4 shadow-xl border-2 border-blue-100/50 relative"
                    variants={floatingVariants}
                    animate="float"
                  >
                    <p className="text-center text-gray-700 font-medium leading-relaxed whitespace-pre-line text-sm md:text-base">
                      {reactiveMessage}
                    </p>
                    
                    {/* Speech bubble tail pointing up to pet */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[16px] border-r-[16px] border-b-[16px] border-l-transparent border-r-transparent border-b-blue-50/95"></div>
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[18px] border-r-[18px] border-b-[18px] border-l-transparent border-r-transparent border-b-blue-100/50"></div>
                  </motion.div>

                  {/* Floating Stars around Reactive Bubble */}
                  <div className="absolute inset-0 pointer-events-none">
                    {[...Array(3)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute text-yellow-400"
                        style={{
                          left: `${10 + i * 40}%`,
                          top: `${10 + i * 25}%`,
                          fontSize: '12px'
                        }}
                        animate={{
                          y: [0, -10, 0],
                          opacity: [0.7, 1, 0.7],
                          scale: [0.9, 1.2, 0.9],
                          rotate: [0, 10, -10, 0]
                        }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                          delay: i * 0.4,
                          ease: "easeInOut"
                        }}
                      >
                        ⭐
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomeBubble;
